import ModalComponent from 'components/Common/Modal';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import ClipPreviewPlayer from './ClipCreation/ClipPreviewPlayer';
import ClipTrimmer from './ClipCreation/ClipTrimmer';
import { computeClipWindow } from './ClipCreation/clipWindow';
import { generateClip, TARGET_VARIANT_HEIGHT } from './ClipCreation/generateClip';

const RESOLUTION_OPTIONS = [
  { value: 480, label: 'Standard (480p)' },
  { value: 720, label: 'High quality (720p)' },
];

// Checkpoints mirror PHASE_PERCENT in generateClip.js; the live `message` drives the headline.
const CLIP_PROGRESS_STAGES = [
  { label: 'Fetching footage', at: 0 },
  { label: 'Loading video engine', at: 20 },
  { label: 'Encoding clip', at: 35 },
];

const ClipProgress = ({ percent, message, remainingSeconds }) => {
  const value = Math.max(0, Math.min(100, Math.round(percent)));
  const done = value >= 100;
  let stageIndex = 0;
  CLIP_PROGRESS_STAGES.forEach((stage, i) => {
    if (value >= stage.at) stageIndex = i;
  });
  const dotLeft = Math.max(0.6, Math.min(98.4, value)) - 0.6;

  return (
    <div className='clip-progress'>
      <div className='clip-progress-head'>
        <p className='clip-progress-message mb-0'>{message || CLIP_PROGRESS_STAGES[stageIndex].label}</p>
        <span className='clip-progress-percent'>{value}%</span>
      </div>
      <div className='clip-progress-track'>
        <div className='clip-progress-fill' style={{ width: `${value}%` }}>
          <span className='clip-progress-sheen' />
        </div>
        <span className='clip-progress-dot' style={{ left: `${dotLeft}%` }} />
      </div>
      <ul className='clip-progress-stages'>
        {CLIP_PROGRESS_STAGES.map((stage, i) => {
          const complete = done || i < stageIndex;
          const current = !done && i === stageIndex;
          return (
            <li
              key={stage.label}
              className={`clip-progress-stage${complete ? ' is-complete' : ''}${current ? ' is-current' : ''}`}
            >
              <span className='clip-progress-stage-marker'>{complete ? '✓' : ''}</span>
              {stage.label}
            </li>
          );
        })}
      </ul>
      {/* {remainingSeconds != null && !done && (
        <p className='clip-progress-remaining mb-0'>~{remainingSeconds}s remaining</p>
      )} */}
    </div>
  );
};

const SelectDropdown = ({ wrapperClass, label, value, options, onSelect }) => {
  const selected = options.find((option) => option.value === value);
  return (
    <div className={wrapperClass}>
      <label className='line-height-1-1 font-weight-bold paragraph-p1 d-block mb-2'>{label}</label>
      <DropdownComponent mainItem={selected?.label ?? ''} mainItemClass='text-left' fixedDropdown>
        <ul className='policy-list p-0 m-0'>
          {options.map((option) => (
            <li key={option.value}>
              <button
                className='w-100 d-block text-left general-button list-action-button pl-3 specific-right-padding-40 py-2 reset-link reset-link-color text-main-blue nowrap-item position-relative'
                onClick={() => onSelect(option.value)}
              >
                {option.label}
                {option.value === value && <span className='icon-tick position-absolute top-10 right-11' />}
              </button>
            </li>
          ))}
        </ul>
      </DropdownComponent>
    </div>
  );
};

const CreateClipModal = (props) => {
  const {
    isOpen,
    setIsOpen,
    masterPlaylistUrl,
    logoUrl,
    item,
    itemVideoStart,
    itemVideoEnd,
    videoDuration,
    initialThumbnails = [],
    poster,
  } = props;

  const [resolutionOption, setResolutionOption] = useState(TARGET_VARIANT_HEIGHT);
  const [status, setStatus] = useState('idle'); // idle | loading | error
  const [progress, setProgress] = useState({ message: '', percent: null, remainingSeconds: null });
  const [clipUrl, setClipUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [clipRange, setClipRange] = useState({ start: 0, end: 0 });
  const clipUrlRef = useRef(null);
  const downloadLinkRef = useRef(null);
  const previewPlayerRef = useRef(null);

  const clipMoment = item?.dateTime && dayjs(item.dateTime).isValid() ? dayjs(item.dateTime) : dayjs();
  const clipFileName = `PolicyMogul_clip_${clipMoment.format('D_MMM_HH.mm')}.mp4`;

  const {
    clipStart: initialClipStart,
    clipEnd: initialClipEnd,
    windowStart,
    windowEnd,
  } = computeClipWindow({
    itemVideoStart,
    itemVideoEnd,
    videoDuration,
  });

  const setClip = (blob) => {
    if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    const url = URL.createObjectURL(blob);
    clipUrlRef.current = url;
    setClipUrl(url);
  };

  // No "finished clip" screen — the download starts on its own and the trimmer stays put, ready
  // for another clip.
  const runGenerate = async () => {
    setStatus('loading');
    setProgress({ message: '', percent: null, remainingSeconds: null });
    try {
      const blob = await generateClip({
        masterPlaylistUrl,
        startSeconds: clipRange.start,
        endSeconds: clipRange.end,
        logoUrl,
        targetHeight: resolutionOption,
        onProgress: setProgress,
      });
      setClip(blob);
      setStatus('idle');
    } catch (error) {
      setProgress({
        message: error?.message ?? 'Something went wrong generating the clip.',
        percent: null,
        remainingSeconds: null,
      });
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setStatus('idle');
      setClipUrl(null);
      setIsPlaying(false);
    }
  }, [isOpen, item]);

  useEffect(() => {
    setClipRange({ start: initialClipStart, end: initialClipEnd });
  }, [item, initialClipStart, initialClipEnd]);

  useEffect(() => {
    return () => {
      if (clipUrlRef.current) URL.revokeObjectURL(clipUrlRef.current);
    };
  }, []);

  const handleGenerateAndDownload = async () => {
    await runGenerate();
    setTimeout(() => downloadLinkRef.current?.click(), 0);
  };

  return (
    <ModalComponent isOpen={isOpen} setIsOpen={setIsOpen} maxWidth={900}>
      <h3 className='title-h4-bold mb-0 px-4 py-3 border-bottom'>Create a clip</h3>
      <div className='p-4'>
        <div className='d-lg-flex'>
          <div className='flex-grow-1 pr-lg-4 mb-3 mb-lg-0' style={{ minWidth: 0 }}>
            <div className='position-relative bg-main-blue rounded overflow-hidden' style={{ aspectRatio: '16/9' }}>
              {status === 'loading' ? (
                <div className='w-100 h-100 d-flex align-items-center justify-content-center p-3'>
                  <ClipProgress
                    percent={progress.percent ?? 0}
                    message={progress.message}
                    remainingSeconds={progress.remainingSeconds}
                  />
                </div>
              ) : status === 'error' ? (
                <div className='w-100 h-100 flex-centered justify-content-center text-white text-center px-3'>
                  {progress.message}
                </div>
              ) : isOpen ? (
                <ClipPreviewPlayer
                  key={`${item?.objectID ?? 'clip'}-${windowStart}-${windowEnd}`}
                  ref={previewPlayerRef}
                  masterPlaylistUrl={masterPlaylistUrl}
                  windowStart={windowStart}
                  windowEnd={windowEnd}
                  clipStart={clipRange.start}
                  clipEnd={clipRange.end}
                  logoUrl={logoUrl}
                  poster={poster}
                  onPlayingChange={setIsPlaying}
                  onTimeUpdate={setCurrentTime}
                  skipThumbnailCapture
                />
              ) : null}
            </div>
            {status === 'idle' && isOpen && (
              <div className='mt-3'>
                <ClipTrimmer
                  thumbnails={initialThumbnails}
                  windowStart={windowStart}
                  windowEnd={windowEnd}
                  clipStart={clipRange.start}
                  clipEnd={clipRange.end}
                  currentTime={currentTime}
                  isPlaying={isPlaying}
                  onTogglePlay={() => previewPlayerRef.current?.togglePlay()}
                  onChange={({ start, end, playhead }) => {
                    if (playhead != null) previewPlayerRef.current?.showFrame(playhead);
                    setClipRange({ start, end });
                  }}
                />
              </div>
            )}
          </div>
          <div style={{ minWidth: '300px' }}>
            <SelectDropdown
              wrapperClass='mb-3'
              label='Video quality'
              value={resolutionOption}
              options={RESOLUTION_OPTIONS}
              onSelect={setResolutionOption}
            />
          </div>
        </div>
      </div>
      <div className='border-top rounded-bottom px-4 py-3 d-flex justify-content-end bg-salmon'>
        <button
          className='general-button action-button rounded-pill px-4 specific-y-padding-5'
          disabled={status === 'loading'}
          onClick={handleGenerateAndDownload}
        >
          {status === 'loading' ? progress.message || 'Generating…' : 'Generate clip'}
        </button>
        {clipUrl && (
          <a ref={downloadLinkRef} href={clipUrl} download={clipFileName} className='d-none'>
            download
          </a>
        )}
      </div>
    </ModalComponent>
  );
};

export default CreateClipModal;
